import AuthSurface from '../components/AuthSurface';
import React, { useState } from 'react';
import { authVisualPreview } from '../lib/authVisualPreview';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL as API_URL } from '../apiBase';
import { Loader2, ArrowLeft, ArrowRight, ShieldCheck } from 'lucide-react';
import { formatApiError } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import AuthCardHeader, { AuthThemeSlot } from '../components/AuthCardHeader';

const ForgotPassword = () => {
  const { resolved } = useTheme();
  const authVisual = authVisualPreview();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRequest = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setInfo(data?.message || 'Se o e-mail existir, enviaremos um código por WhatsApp.');
      setStep(2);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirm) {
      setError('As senhas não coincidem.');
      return;
    }
    if ((newPassword || '').length < 10 || !/[A-Za-z]/.test(newPassword) || !/\d/.test(newPassword)) {
      setError('Use pelo menos 10 caracteres, incluindo letras e números.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        email,
        code: code.replace(/\s/g, ''),
        new_password: newPassword,
      });
      setInfo('Senha alterada! Você já pode entrar.');
      setStep(3);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const title =
    step === 3 ? 'Senha atualizada' : step === 2 ? 'Nova senha' : 'Recuperar senha';
  const subtitle =
    step === 1
      ? 'Informe o e-mail da conta. Se ele estiver cadastrado, enviaremos um código ao WhatsApp vinculado.'
      : step === 2
        ? 'Confirme o código de 6 dígitos e escolha uma senha forte com letras e números.'
        : 'Sua senha foi protegida e atualizada. Entre novamente para continuar.';

  return (
    <AuthSurface className="login-aurax login-aurax--solo" data-theme={resolved} data-auth-visual={authVisual} data-testid="forgot-password-page">
      <section className="login-aurax__panel">
        <AuthThemeSlot visual={authVisual} />

        <div className="login-aurax__card">
          <AuthCardHeader visual={authVisual}>
            <div className="login-aurax__card-brand">
              <span className="login-aurax__mark login-aurax__mark--lg" aria-hidden>
                A
              </span>
            </div>
          </AuthCardHeader>

          <Link to="/login" className="login-aurax__back">
            <ArrowLeft size={14} strokeWidth={2} /> Voltar ao login
          </Link>

          <div className="login-aurax__intro">
            <h2 className="login-aurax__title">{title}</h2>
            <p className="login-aurax__subtitle">{subtitle}</p>
          </div>

          {error ? (
            <div className="login-aurax__error" role="alert">
              {error}
            </div>
          ) : null}
          {info && step !== 3 ? <div className="login-aurax__info">{info}</div> : null}

          {step === 1 && (
            <form onSubmit={handleRequest} className="login-aurax__form">
              <div className="login-aurax__field">
                <label htmlFor="fp-email" className="login-aurax__label">
                  E-mail
                </label>
                <input
                  id="fp-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="login-aurax__input"
                  placeholder="seu@email.com"
                  autoComplete="username"
                />
              </div>
              <button type="submit" className="login-aurax__submit" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Aguarde...
                  </>
                ) : (
                  <>
                    Enviar código
                    <ArrowRight size={18} strokeWidth={2} />
                  </>
                )}
              </button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleReset} className="login-aurax__form">
              <div className="login-aurax__field">
                <label htmlFor="fp-code" className="login-aurax__label">
                  Código (WhatsApp)
                </label>
                <input
                  id="fp-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="login-aurax__input login-aurax__input--otp"
                  placeholder="000000"
                  maxLength={8}
                  required
                />
              </div>
              <div className="login-aurax__field">
                <label htmlFor="fp-np" className="login-aurax__label">
                  Nova senha
                </label>
                <input
                  id="fp-np"
                  type="password"
                  required
                  minLength={10}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="login-aurax__input"
                  autoComplete="new-password"
                />
              </div>
              <div className="login-aurax__field">
                <label htmlFor="fp-cf" className="login-aurax__label">
                  Confirmar senha
                </label>
                <input
                  id="fp-cf"
                  type="password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="login-aurax__input"
                  autoComplete="new-password"
                />
              </div>
              <button type="submit" className="login-aurax__submit" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Aguarde...
                  </>
                ) : (
                  <>
                    Redefinir senha
                    <ArrowRight size={18} strokeWidth={2} />
                  </>
                )}
              </button>
            </form>
          )}

          {step === 3 && (
            <div className="login-aurax__form">
              <div className="login-aurax__info">{info}</div>
              <Link to="/login" className="login-aurax__submit" style={{ textDecoration: 'none' }}>
                Ir para o login
                <ArrowRight size={18} strokeWidth={2} />
              </Link>
            </div>
          )}

          <div className="login-aurax__legal">
            <p className="login-aurax__footer">
              <ShieldCheck size={15} strokeWidth={1.9} className="login-aurax__footer-icon" aria-hidden />
              Recuperação protegida · código enviado apenas para o contato da conta
            </p>
          </div>
        </div>
      </section>
    </AuthSurface>
  );
};

export default ForgotPassword;
